import { useForm } from '@tanstack/react-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Student, Class } from '@/interface/types'
import { ExtendedParent as Parent } from '@/interface/types/parent-extended'
import FileUploader from '@/components/shared/FileUploader'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import { Eye, EyeOff, Search, X, Check, ChevronsUpDown } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { FormYearMonthPicker } from '@/components/ui/form-year-month-picker'
import { useTranslation } from 'react-i18next'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

// Helper function to get all nationality keys
const getAllNationalityKeys = (): string[] => {
  return [
    'afghan', 'albanian', 'algerian', 'american', 'andorran', 'angolan', 'antiguans',
    'argentine', 'armenian', 'australian', 'austrian', 'azerbaijani', 'bahamian',
    'bahraini', 'bangladeshi', 'barbadian', 'barbudans', 'batswana', 'belarusian',
    'belgian', 'belizean', 'beninese', 'bhutanese', 'bolivian', 'bosnian', 'brazilian',
    'british', 'bruneian', 'bulgarian', 'burkinabe', 'burmese', 'burundian', 'cambodian',
    'cameroonian', 'canadian', 'cape verdean', 'central african', 'chadian', 'chilean',
    'chinese', 'colombian', 'comoran', 'congolese', 'costa rican', 'croatian', 'cuban',
    'cypriot', 'czech', 'danish', 'djibouti', 'dominican', 'dutch', 'east timorese',
    'ecuadorean', 'egyptian', 'emirian', 'equatorial guinean', 'eritrean', 'estonian',
    'ethiopian', 'fijian', 'filipino', 'finnish', 'french', 'gabonese', 'gambian',
    'georgian', 'german', 'ghanaian', 'greek', 'grenadian', 'guatemalan', 'guinea-bissauan',
    'guinean', 'guyanese', 'haitian', 'herzegovinian', 'honduran', 'hungarian', 'icelander',
    'indian', 'indonesian', 'iranian', 'iraqi', 'irish', 'israeli', 'italian', 'ivorian',
    'jamaican', 'japanese', 'jordanian', 'kazakhstani', 'kenyan', 'kittian and nevisian',
    'kuwaiti', 'kyrgyz', 'laotian', 'latvian', 'lebanese', 'liberian', 'libyan',
    'liechtensteiner', 'lithuanian', 'luxembourger', 'macedonian', 'malagasy', 'malawian',
    'malaysian', 'maldivan', 'malian', 'maltese', 'marshallese', 'mauritanian', 'mauritian',
    'mexican', 'micronesian', 'moldovan', 'monacan', 'mongolian', 'moroccan', 'mosotho',
    'motswana', 'mozambican', 'namibian', 'nauruan', 'nepalese', 'new zealander', 'ni-vanuatu',
    'nicaraguan', 'nigerien', 'north korean', 'northern irish', 'norwegian', 'omani',
    'pakistani', 'palauan', 'panamanian', 'papua new guinean', 'paraguayan', 'peruvian',
    'polish', 'portuguese', 'qatari', 'romanian', 'russian', 'rwandan', 'saint lucian',
    'salvadoran', 'samoan', 'san marinese', 'sao tomean', 'saudi', 'scottish', 'senegalese',
    'serbian', 'seychellois', 'sierra leonean', 'singaporean', 'slovakian', 'slovenian',
    'solomon islander', 'somali', 'south african', 'south korean', 'spanish', 'sri lankan',
    'sudanese', 'surinamer', 'swazi', 'swedish', 'swiss', 'syrian', 'taiwanese', 'tajik',
    'tanzanian', 'thai', 'togolese', 'tongan', 'trinidadian or tobagonian', 'tunisian',
    'turkish', 'tuvaluan', 'ugandan', 'ukrainian', 'uruguayan', 'uzbekistani', 'venezuelan',
    'vietnamese', 'welsh', 'yemenite', 'zambian', 'zimbabwean'
  ]
}

const studentSchema = z.object({
  cin: z.string().min(8, 'CIN must be 8 characters'),
  firstname: z.string().min(2, 'First name must be at least 2 characters'),
  lastname: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email format'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(
      /[!@#$%^&*(),.?":{}|<>]/,
      'Password must contain at least one special character'
    )
    .optional(),
  gradeLevel: z.string().optional(),
  enrolledDate: z.date().optional(),
  parentId: z.string().optional(),
  classId: z.string().optional(),
  birthday: z.date().optional(),
  gender: z.enum(['male', 'female'] as const),
  address: z.string().optional(),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  avatar: z.string().url().optional(),
  isActive: z.boolean().default(true),
})

type StudentFormValues = z.infer<typeof studentSchema>
export type { StudentFormValues }

interface StudentFormProps {
  initialData?: Partial<Student>
  parents: Parent[]
  classes: Class[]
  onSubmit: (data: StudentFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
  fieldErrors?: Record<string, string>
}

/**
 * A reusable form component for creating and editing students
 * @param {StudentFormProps} props - The props for the StudentForm component
 * @returns {JSX.Element} The rendered StudentForm component
 */
export function StudentForm({
  initialData,
  parents,
  classes,
  onSubmit,
  onCancel,
  submitLabel,
  fieldErrors = {},
}: StudentFormProps) {
  const { t } = useTranslation('admin')
  const { t: tNationalities } = useTranslation('nationalities')
  
  // Debug logging to help identify parent ID issues
  // console.log('StudentForm - initialData:', initialData)
  // console.log('StudentForm - initialData.parent:', initialData?.parent)
  // console.log('StudentForm - initialData.nationality:', initialData?.nationality)
  // console.log('StudentForm - parents array:', parents)
  
  // Normalize the gender value from server
  const normalizeGender = (gender: string | undefined): 'male' | 'female' => {
    if (!gender) return 'male' // Default to male if no gender is provided

    // Handle both 'Male'/'Female' and 'male'/'female' formats
    const normalizedGender = gender.toLowerCase()
    if (normalizedGender === 'male' || normalizedGender === 'female') {
      return normalizedGender as 'male' | 'female'
    }

    // Default to male for any other values
    return 'male'
  }

  const [showPassword, setShowPassword] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)

  // State for parent search
  const [parentSearchQuery, setParentSearchQuery] = useState('')
  const [showParentSelector, setShowParentSelector] = useState(false)
  const parentSelectorRef = useRef<HTMLDivElement>(null)

  // Helper function to normalize nationality key (lowercase)
  const normalizeNationalityKey = (nationality: string | undefined): string => {
    if (!nationality) return 'tunisian'
    return nationality.toLowerCase()
  }

  // Nationality search state - initialize with initialData if available
  const [nationalitySearchOpen, setNationalitySearchOpen] = useState(false)
  const [nationalitySearchQuery, setNationalitySearchQuery] = useState('')
  const [selectedNationality, setSelectedNationality] = useState<string>(
    normalizeNationalityKey(initialData?.nationality)
  )

  // Handle click outside to close the parent selector
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        parentSelectorRef.current &&
        !parentSelectorRef.current.contains(event.target as Node)
      ) {
        setShowParentSelector(false)
        // Reset search query when closing the selector
        setParentSearchQuery('')
      }
    }

    // Only add the event listener when the selector is shown
    if (showParentSelector) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [parentSelectorRef, showParentSelector])

  // Filter parents based on search query
  const filteredParents = parents.filter((parent) => {
    const fullName = `${parent.firstname} ${parent.lastname}`.toLowerCase()
    const cin = parent.cin?.toLowerCase() || ''
    const query = parentSearchQuery.toLowerCase()
    return fullName.includes(query) || cin.includes(query)
  })

  // Extract parent ID with better error handling
  const getParentId = () => {
    if (!initialData?.parent) return ''
    
    // Handle different parent data structures
    if (typeof initialData.parent === 'string') {
      return initialData.parent
    }
    
    if (typeof initialData.parent === 'object') {
      // Prioritize userId or user.id over parent.id
      // parent.id is the relationship ID, not the parent user ID that the backend expects
      return initialData.parent.userId || 
             (initialData.parent.user && initialData.parent.user.id) || 
             initialData.parent.id || 
             ''
    }
    
    return ''
  }

  // Calculate date 5 years ago for default and maxDate
  const getFiveYearsAgo = () => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 5)
    return date
  }
  // Set maxDate to 5 years ago, but allow existing birthdays that are less than 5 years ago
  // (for existing users who were created before this validation)
  const fiveYearsAgo = getFiveYearsAgo()
  const existingBirthday = initialData?.birthday ? new Date(initialData.birthday) : null
  const maxBirthdayDate = existingBirthday && existingBirthday > fiveYearsAgo 
    ? existingBirthday 
    : fiveYearsAgo

  const form = useForm({
    defaultValues: {
      firstname: initialData?.firstname || '',
      lastname: initialData?.lastname || '',
      email: initialData?.email || '',
      cin: initialData?.cin || '',
      gradeLevel: initialData?.gradeLevel || '',
      gender: normalizeGender(initialData?.gender),
      birthday: initialData?.birthday
        ? new Date(initialData.birthday)
        : getFiveYearsAgo(),
      enrolledDate: initialData?.enrolledDate
        ? new Date(initialData.enrolledDate)
        : new Date(),
      // Initialize parentId with better error handling
      parentId: getParentId(),
      classId: initialData?.class?.id ? String(initialData.class.id) : '',
      address: initialData?.address || '',
      phone: initialData?.phone || '',
      nationality: normalizeNationalityKey(initialData?.nationality),
      isActive: initialData?.isActive ?? true,
      // Initialize password as empty string to avoid controlled/uncontrolled warning
      password: '',
      avatar: initialData?.avatar || '',
    },
    onSubmit: async ({ value }) => {
   // console.logStudentForm - submitting with value:', value)
      const formData = {
        ...value,
        nationality: selectedNationality || value.nationality || 'tunisian',
      }
      await onSubmit(formData)
    },
  })

  // Sync selected nationality when initialData changes (after form is created)
  useEffect(() => {
    const nationality = normalizeNationalityKey(initialData?.nationality)
 // console.logStudentForm - Syncing nationality:', nationality, 'from initialData:', initialData?.nationality)
    setSelectedNationality(nationality)
    // Also update the form field value
    form.setFieldValue('nationality', nationality)
  }, [initialData?.nationality, form])
  
  // Sync field value when selectedNationality changes
  useEffect(() => {
    if (selectedNationality) {
   // console.logStudentForm - Setting form field nationality to:', selectedNationality)
      form.setFieldValue('nationality', selectedNationality)
    }
  }, [selectedNationality, form])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="cin"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('student-folder.studentForm.fields.cin.validation.required')
              if (value.length !== 8) return t('student-folder.studentForm.fields.cin.validation.length')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="cin">{t('student-folder.studentForm.fields.cin.label')}</Label>
              <Input
                id="cin"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.cin.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
              {fieldErrors.cin && (
                <p className="text-sm text-red-500">{fieldErrors.cin}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="firstname"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('student-folder.studentForm.fields.firstname.validation.required')
              if (value.length < 2)
                return t('student-folder.studentForm.fields.firstname.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="firstname">{t('student-folder.studentForm.fields.firstname.label')}</Label>
              <Input
                id="firstname"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.firstname.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
              {fieldErrors.firstname && (
                <p className="text-sm text-red-500">{fieldErrors.firstname}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="lastname"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('student-folder.studentForm.fields.lastname.validation.required')
              if (value.length < 2)
                return t('student-folder.studentForm.fields.lastname.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="lastname">{t('student-folder.studentForm.fields.lastname.label')}</Label>
              <Input
                id="lastname"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.lastname.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
              {fieldErrors.lastname && (
                <p className="text-sm text-red-500">{fieldErrors.lastname}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="email"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('student-folder.studentForm.fields.email.validation.required')
              if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                return t('student-folder.studentForm.fields.email.validation.invalidFormat')
              }
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="email">{t('student-folder.studentForm.fields.email.label')}</Label>
              <Input
                id="email"
                type="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.email.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
              {fieldErrors.email && (
                <p className="text-sm text-red-500">{fieldErrors.email}</p>
              )}
            </div>
          )}
        </form.Field>

        {!initialData ? (
          <form.Field
            name="password"
            validators={{
              onChange: ({ value }) => {
                if (!value) return t('student-folder.studentForm.fields.password.validation.required')
                if (value.length < 6)
                  return t('student-folder.studentForm.fields.password.validation.minLength')
                if (!/[A-Z]/.test(value))
                  return t('student-folder.studentForm.fields.password.validation.uppercase')
                if (!/[0-9]/.test(value))
                  return t('student-folder.studentForm.fields.password.validation.number')
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value))
                  return t('student-folder.studentForm.fields.password.validation.specialChar')
                return undefined
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="password">{t('student-folder.studentForm.fields.password.label')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('student-folder.studentForm.fields.password.placeholder')}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center"
                    tabIndex={-1}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
                {fieldErrors.password && (
                  <p className="text-sm text-red-500">{fieldErrors.password}</p>
                )}
              </div>
            )}
          </form.Field>
        ) : (
          <form.Field
            name="password"
            validators={{
              onBlur: ({ value }) => {
                // Skip validation if empty (keeping current password)
                if (!value || value.trim() === '') return undefined

                // Validate password only if provided
                const errors = []
                if (value.length < 6) {
                  errors.push(t('student-folder.studentForm.fields.password.validation.minLength'))
                }
                if (!/[A-Z]/.test(value)) {
                  errors.push(
                    t('student-folder.studentForm.fields.password.validation.uppercase')
                  )
                }
                if (!/[0-9]/.test(value)) {
                  errors.push(t('student-folder.studentForm.fields.password.validation.number'))
                }
                if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                  errors.push(
                    t('student-folder.studentForm.fields.password.validation.specialChar')
                  )
                }

                return errors.length > 0 ? errors.join(', ') : undefined
              },
            }}
          >
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="password">{t('student-folder.studentForm.fields.password.label')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={field.state.value}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => {
                      field.handleBlur()
                      setIsPasswordFocused(false)
                    }}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('student-folder.studentForm.fields.password.placeholder')}
                    className={
                      field.state.meta.isTouched && field.state.meta.errors
                        ? 'border-red-500'
                        : ''
                    }
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-2 flex items-center"
                    tabIndex={-1}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {field.state.meta.errors && (
                  <p className="text-sm text-red-500">
                    {field.state.meta.errors.join(', ')}
                  </p>
                )}
                {isPasswordFocused && (
                  <p className="text-xs text-gray-500">
                    {t('student-folder.studentForm.fields.password.validation.keepCurrent')}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        )}

        <form.Field name="gradeLevel">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="gradeLevel">{t('student-folder.studentForm.fields.gradeLevel.label')}</Label>
              <Input
                id="gradeLevel"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.gradeLevel.placeholder')}
              />
              {fieldErrors.gradeLevel && (
                <p className="text-sm text-red-500">{fieldErrors.gradeLevel}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="enrolledDate">
          {(field) => (
            <FormYearMonthPicker
              id="enrolledDate"
              label={t('student-folder.studentForm.fields.enrolledDate.label')}
              value={field.state.value}
              onChange={(date) => field.handleChange(date as any)}
              onBlur={field.handleBlur}
              error={fieldErrors.enrolledDate}
              placeholder={t('student-folder.studentForm.fields.enrolledDate.placeholder')}
            />
          )}
        </form.Field>

        <form.Field 
          name="birthday"
          validators={{
            onChange: ({ value }) => {
              if (!value) return undefined // Optional field
              
              const today = new Date()
              const birthDate = new Date(value)
              let age = today.getFullYear() - birthDate.getFullYear()
              const monthDiff = today.getMonth() - birthDate.getMonth()
              
              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--
              }
              
              if (age < 5) {
                return t('student-folder.studentForm.fields.birthday.validation.minAge')
              }
              
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <FormYearMonthPicker
                id="birthday"
                label={t('student-folder.studentForm.fields.birthday.label')}
                value={field.state.value}
                onChange={(date) => field.handleChange(date || getFiveYearsAgo())}
                onBlur={field.handleBlur}
                error={fieldErrors.birthday}
                placeholder={t('student-folder.studentForm.fields.birthday.placeholder')}
                maxDate={maxBirthdayDate}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
              {fieldErrors.birthday && (
                <p className="text-sm text-red-500">{fieldErrors.birthday}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="gender"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('student-folder.studentForm.fields.gender.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="gender">{t('student-folder.studentForm.fields.gender.label')}</Label>
              <Select
                value={field.state.value}
                onValueChange={(value: 'male' | 'female') =>
                  field.handleChange(value)
                }
              >
                <SelectTrigger id="gender">
                  <SelectValue placeholder={t('student-folder.studentForm.fields.gender.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('student-folder.studentForm.fields.gender.male')}</SelectItem>
                  <SelectItem value="female">{t('student-folder.studentForm.fields.gender.female')}</SelectItem>
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
              {fieldErrors.gender && (
                <p className="text-sm text-red-500">{fieldErrors.gender}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="parentId">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="parentId">{t('student-folder.studentForm.fields.parent.label')}</Label>
              <div className="relative" ref={parentSelectorRef}>
                <div
                  className="flex items-center border rounded-md p-2 cursor-pointer"
                  onClick={() => setShowParentSelector((prev) => !prev)}
                >
                  {field.state.value ? (
                    <div className="flex-1">
                      {(() => {
                        const selectedParent = parents.find((p) => p.id === field.state.value)
                        if (selectedParent) {
                          return `${selectedParent.firstname} ${selectedParent.lastname}`
                        }

                        // Fallback to the name embedded in initialData.parent.user if available
                        // field.state.value should be userId or user.id (not the relationship id)
                        const p: any = initialData?.parent
                        const matchesInitial = p && (
                          p.userId === field.state.value ||
                          (p.user && p.user.id === field.state.value)
                        )
                        if (matchesInitial) {
                          const first = p.user?.firstname ?? p.firstname ?? ''
                          const last = p.user?.lastname ?? p.lastname ?? ''
                          const full = `${first} ${last}`.trim()
                          if (full) return full
                        }

                        return `${t('student-folder.studentForm.fields.parent.notFound')} ${field.state.value}`
                      })()}
                    </div>
                  ) : (
                    <div className="flex-1 text-gray-500">{t('student-folder.studentForm.fields.parent.select')}</div>
                  )}
                  {/* <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 p-0 left-[-50px]"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (field.state.value) {
                        field.handleChange('')
                      }
                    }}
                  >
                    {field.state.value ? t('student-folder.studentForm.fields.parent.clear') : t('student-folder.studentForm.fields.parent.select')}
                  </Button> */}
                </div>

                {showParentSelector && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg">
                    <div className="p-2 relative">
                      <Input
                        placeholder={t('student-folder.studentForm.fields.parent.searchPlaceholder')}
                        value={parentSearchQuery}
                        onChange={(e) => setParentSearchQuery(e.target.value)}
                        className="w-full pl-9"
                      />
                      <div className="absolute inset-y-0 left-0 flex items-center pl-5 pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                      {parentSearchQuery && (
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center pr-5"
                          onClick={() => setParentSearchQuery('')}
                        >
                          <X className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                    </div>
                    <ScrollArea className="h-[200px] w-full">
                      <div className="space-y-2 p-2">
                        {filteredParents.length > 0 ? (
                          filteredParents.map((parent) => (
                            <div
                              key={parent.id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                              onClick={() => {
                             // console.logSelecting parent:', parent.id, parent.firstname, parent.lastname)
                                field.handleChange(parent.id)
                                setShowParentSelector(false)
                              }}
                            >
                              <Checkbox
                                checked={field.state.value === parent.id}
                                onCheckedChange={() => {
                               // console.logCheckbox clicked for parent:', parent.id, parent.firstname, parent.lastname)
                                  field.handleChange(parent.id)
                                  setShowParentSelector(false)
                                }}
                                className="flex-shrink-0"
                              />
                              <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                <CdnImage
                                  src={parent.avatar || DEFAULT_AVATAR}
                                  alt={`${parent.firstname} ${parent.lastname}`}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <span className="flex-1">
                                {parent.firstname} {parent.lastname}{' '}
                                {parent.cin && `(${parent.cin})`}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="p-2 text-center text-gray-500">
                            {parentSearchQuery
                              ? t('student-folder.studentForm.fields.parent.noMatch')
                              : t('student-folder.studentForm.fields.parent.noParents')}
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                    <div className="p-2 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          field.handleChange('')
                          setShowParentSelector(false)
                        }}
                      >
                        {t('student-folder.studentForm.fields.parent.clearSelection')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {fieldErrors.parentId && (
                <p className="text-sm text-red-500">{fieldErrors.parentId}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="classId">
          {(field) => {
            // Ensure value is always a string or empty string
            const value = field.state.value || '';
            
            return (
              <div className="space-y-2">
                <Label htmlFor="classId">{t('student-folder.studentForm.fields.class.label')}</Label>
                <Select
                  value={value || 'none'}
                  onValueChange={(selectedValue: string) => {
                    // Convert 'none' to empty string, otherwise keep the value as is
                    const newValue = selectedValue === 'none' ? '' : selectedValue;
                 // console.logClass selected:', { selectedValue, newValue });
                    field.handleChange(newValue);
                  }}
                >
                  <SelectTrigger id="classId" className="w-full">
                    <SelectValue placeholder={t('student-folder.studentForm.fields.class.placeholder')} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    <SelectItem value="none">{t('student-folder.studentForm.fields.class.noClass')}</SelectItem>
                    {classes.slice(0, 100).map((cls) => {
                      const classId = String(cls.id);
                      return (
                        <SelectItem key={classId} value={classId}>
                          {cls.name || `${t('student-folder.studentForm.fields.class.className')} ${classId}`}
                        </SelectItem>
                      );
                    })}
                    {classes.length > 100 && (
                      <div className="py-2 px-2 text-xs text-gray-500">
                        + {classes.length - 100} {t('student-folder.studentForm.fields.class.moreClasses')}
                      </div>
                    )}
                  </SelectContent>
                </Select>
                {fieldErrors.classId && (
                  <p className="text-sm text-red-500">{fieldErrors.classId}</p>
                )}
              </div>
            );
          }}
        </form.Field>

        <form.Field name="address">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="address">{t('student-folder.studentForm.fields.address.label')}</Label>
              <Input
                id="address"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.address.placeholder')}
              />
              {fieldErrors.address && (
                <p className="text-sm text-red-500">{fieldErrors.address}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="phone">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="phone">{t('student-folder.studentForm.fields.phone.label')}</Label>
              <Input
                id="phone"
                type="tel"
                value={field.state.value || ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('student-folder.studentForm.fields.phone.placeholder')}
              />
              {fieldErrors.phone && (
                <p className="text-sm text-red-500">{fieldErrors.phone}</p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field name="nationality">
          {(field) => {
            // Use selectedNationality as primary source, fallback to field value, then initialData, then default
            // Normalize all values to lowercase for comparison
            const normalizedFieldValue = field.state.value ? normalizeNationalityKey(field.state.value) : null
            const normalizedInitialData = initialData?.nationality ? normalizeNationalityKey(initialData.nationality) : null
            const currentNationality = selectedNationality || normalizedFieldValue || normalizedInitialData || 'tunisian'
            
            return (
              <div className="space-y-2">
                <Label htmlFor="nationality">
                  {t('student-folder.studentForm.fields.nationality.label')}
                </Label>
                <Popover open={nationalitySearchOpen} onOpenChange={setNationalitySearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={nationalitySearchOpen}
                      className="w-full justify-between"
                      type="button"
                    >
                      {(() => {
                        const displayValue = currentNationality
                        if (displayValue && getAllNationalityKeys().includes(displayValue)) {
                          return tNationalities(`nationalities.${displayValue}`, displayValue)
                        }
                        return t('student-folder.studentForm.fields.nationality.placeholder')
                      })()}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <div className="p-2">
                      <Input
                        placeholder={t('student-folder.studentForm.fields.nationality.placeholder')}
                        value={nationalitySearchQuery}
                        onChange={(e) => setNationalitySearchQuery(e.target.value)}
                        className="mb-2"
                      />
                    </div>
                    <ScrollArea className="h-[200px]">
                      <div className="p-1">
                        {getAllNationalityKeys()
                          .filter((key) => {
                            if (!nationalitySearchQuery) return true
                            const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                            const searchLower = nationalitySearchQuery.toLowerCase()
                            return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                          })
                          .map((key) => {
                            const translated = tNationalities(`nationalities.${key}`, key)
                            const isSelected = currentNationality === key
                            return (
                              <button
                                key={key}
                                type="button"
                                className={cn(
                                  'w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                                  isSelected && 'bg-accent'
                                )}
                                onClick={() => {
                                  setSelectedNationality(key)
                                  field.handleChange(key)
                                  setNationalitySearchQuery('')
                                  setNationalitySearchOpen(false)
                                }}
                              >
                                <span>{translated}</span>
                                {isSelected && <Check className="h-4 w-4" />}
                              </button>
                            )
                          })}
                        {nationalitySearchQuery && getAllNationalityKeys().filter((key) => {
                          const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                          const searchLower = nationalitySearchQuery.toLowerCase()
                          return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                        }).length === 0 && (
                          <div className="px-2 py-1.5 text-sm text-gray-500">
                            No nationality found
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              </div>
            )
          }}
        </form.Field>

        <form.Field name="avatar">
          {(field) => (
            <FileUploader
              label={t('student-folder.studentForm.fields.avatar.label')}
              defaultPreview={field.state.value || DEFAULT_AVATAR}
              onFileUploaded={(url: string) => field.handleChange(url)}
              isAvatar={true}
              previewComponent={(previewUrl: string) => (
                <CdnImage
                  src={previewUrl}
                  alt={t('student-folder.studentForm.fields.avatar.alt')}
                  className="h-28 w-28 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                />
              )}
            />
          )}
        </form.Field>

        <form.Field name="isActive">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="isActive">{t('student-folder.studentForm.fields.isActive.label')}</Label>
              <Select
                value={field.state.value.toString()}
                onValueChange={(value) => field.handleChange(value === 'true')}
              >
                <SelectTrigger id="isActive">
                  <SelectValue placeholder={t('student-folder.studentForm.fields.isActive.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t('student-folder.studentForm.fields.isActive.active')}</SelectItem>
                  <SelectItem value="false">{t('student-folder.studentForm.fields.isActive.inactive')}</SelectItem>
                </SelectContent>
              </Select>
              {fieldErrors.isActive && (
                <p className="text-sm text-red-500">{fieldErrors.isActive}</p>
              )}
            </div>
          )}
        </form.Field>
      </div>

      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('student-folder.studentForm.buttons.cancel')}
        </Button>
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  )
}
